import { useFlashScanIcons } from "@/components/r3f/renderers/odometry-paths/use-flash-scan-icons";
import Photo2DSelected from "@/components/ui/icons/2Dphoto-selected.svg";
import Photo3602DImageHovered from "@/components/ui/icons/360photo-2d-image-hover.svg";
import Photo3602DImageSelected from "@/components/ui/icons/360photo-2d-image-selected.svg";
import Photo3602DImage from "@/components/ui/icons/360photo-2d-image.svg";
import { IElementWaypoint } from "@/hooks/use-placeholders";
import { selectActiveElementId } from "@/store/selections-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  DEFAULT_WAYPOINT_TEXTURE_SIZE,
  FlagMarker,
  FlagMarkerHovered,
  FlagMarkerSelected,
  Image2D,
  Image2DHovered,
  IMapPlaceholderVariants,
  useSvg,
  WaypointFlashScanSelected,
  WaypointSelected,
} from "@faro-lotv/app-component-toolbox";
import { isIElementRoomSection } from "@faro-lotv/ielement-types";
import { useMemo } from "react";

/** @returns the texture variants for the diverse waypoint types */
export function useWaypointsTextures(): {
  panoImgTextures: Partial<IMapPlaceholderVariants>;
  imgTextures: Partial<IMapPlaceholderVariants>;
  markerTextures: Partial<IMapPlaceholderVariants>;
  waypointTextures: Partial<IMapPlaceholderVariants>;
  flashWaypointTextures: Partial<IMapPlaceholderVariants>;
} {
  const panoImgTextures = usePanoImgTexture();
  const imgTextures = useImgTexture();
  const markerTextures = useMarkerTexture();
  const waypointTextures = useWaypointTexture();
  const flashWaypointTextures = useFlashWaypointTexture();

  return {
    panoImgTextures,
    imgTextures,
    markerTextures,
    waypointTextures,
    flashWaypointTextures,
  };
}

const colorsToReplace = {
  "#1F65F0": "red",
  "#082A6E": "red",
};

function useWaypointTexture(): Partial<IMapPlaceholderVariants> {
  const selectedTexture = useSvg(WaypointSelected, {
    width: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    height: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    generateMipmaps: true,
    colorsToReplace,
  });

  return { selectedTexture };
}

function useFlashWaypointTexture(): Partial<IMapPlaceholderVariants> {
  const selectedTexture = useSvg(WaypointFlashScanSelected, {
    width: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    height: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    generateMipmaps: true,
    colorsToReplace,
  });

  return { selectedTexture };
}

function useMarkerTexture(): Partial<IMapPlaceholderVariants> {
  const defaultTexture = useSvg(FlagMarker, {
    width: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    height: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    generateMipmaps: true,
    colorsToReplace,
  });
  const selectedTexture = useSvg(FlagMarkerSelected, {
    width: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    height: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    generateMipmaps: true,
    colorsToReplace,
  });
  const hoveredTexture = useSvg(FlagMarkerHovered, {
    width: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    height: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    generateMipmaps: true,
    colorsToReplace,
  });

  return { defaultTexture, hoveredTexture, selectedTexture };
}

function usePanoImgTexture(): Partial<IMapPlaceholderVariants> {
  const defaultTexture = useSvg(Photo3602DImage, {
    width: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    height: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    generateMipmaps: true,
    colorsToReplace,
  });

  const hoveredTexture = useSvg(Photo3602DImageHovered, {
    width: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    height: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    generateMipmaps: true,
    colorsToReplace,
  });

  const selectedTexture = useSvg(Photo3602DImageSelected, {
    width: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    height: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    generateMipmaps: true,
    colorsToReplace,
  });
  return { defaultTexture, hoveredTexture, selectedTexture };
}

function useImgTexture(): Partial<IMapPlaceholderVariants> {
  const defaultTexture = useSvg(Image2D, {
    width: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    height: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    generateMipmaps: true,
    colorsToReplace,
  });

  const hoveredTexture = useSvg(Image2DHovered, {
    width: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    height: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    generateMipmaps: true,
    colorsToReplace,
  });

  const selectedTexture = useSvg(Photo2DSelected, {
    width: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    height: DEFAULT_WAYPOINT_TEXTURE_SIZE,
    generateMipmaps: true,
    colorsToReplace,
  });

  return { defaultTexture, hoveredTexture, selectedTexture };
}

type ActiveWaypointWithIndex = {
  activeWaypoint?: IElementWaypoint;
  activeWaypointIndex?: number;
  activeLocationMarker?: IElementWaypoint;
  activeLocationMarkerIndex?: number;
};

/**
 * @returns The active waypoint and its index in the list of waypoints, if any.
 * @param waypoints The list of waypoints being displayed
 */
export function useActiveWaypointIndex(
  waypoints: IElementWaypoint[],
): ActiveWaypointWithIndex {
  const activeElementId = useAppSelector(selectActiveElementId);

  return useMemo(() => {
    const activeWaypointIndex = waypoints.findIndex(
      (waypoint) => waypoint.id === activeElementId,
    );

    if (activeWaypointIndex === -1) {
      return {};
    }

    const activeWaypoint = waypoints[activeWaypointIndex];
    const activeLocationMarker = isIElementRoomSection(activeWaypoint)
      ? activeWaypoint
      : undefined;

    return {
      activeWaypointIndex,
      activeWaypoint,
      activeLocationMarker,
      activeLocationMarkerIndex: activeLocationMarker
        ? activeWaypointIndex
        : -1,
    };
  }, [waypoints, activeElementId]);
}

/**
 * Returns the effective flash textures to use for flash scans.
 * Ensures all texture variants are always defined.
 *
 * @param flashTextures Optional partial flash textures to override defaults.
 * @returns An object with defaultTexture, hoveredTexture, and selectedTexture guaranteed.
 */
export function useEffectiveFlashTextures(
  flashTextures?: Partial<IMapPlaceholderVariants>,
): Required<IMapPlaceholderVariants> {
  const { flashDefault, flashHovered } = useFlashScanIcons();

  return useMemo(
    () => ({
      defaultTexture: flashTextures?.defaultTexture ?? flashDefault,
      hoveredTexture: flashTextures?.hoveredTexture ?? flashHovered,
      selectedTexture: flashTextures?.selectedTexture ?? flashHovered,
    }),
    [flashTextures, flashDefault, flashHovered],
  );
}
